import { useCallback, useState } from "react";

import { ApolloError } from "@apollo/client";
import { datadogRum } from "@datadog/browser-rum";
import { useShallow } from "zustand/react/shallow";

import { CommonListType, InventoryChangeReason } from "__graphql__/types";
import { useInboundEventTracker } from "flows/Inbound/hooks/useInboundEventTracker/useInboundEventTracker";
import { useInboundStore } from "flows/Inbound/stores/inboundStore/useInboundStore";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useBatchUpdateStockForSkuMutation } from "shared/queries/inventoryEntry/inventoryEntry.generated";

import { StockUpdatePlan, ProductComplaintMetadata } from "./inboundStore/types";
import {
  useDeleteInboundingListProductsMutation,
  useEditProductsInInboundingListMutation,
} from "../queries/collaborativeInbound/collaborativeInbound.generated";

const PRODUCT_NOT_ASSIGNED_ERROR = "product-not-assigned-error";
const UNABLE_TO_CREATE_SKU_HUB_ASSOCIATION_ERROR = "unable-create-sku-hub-association-error";
const UPDATE_INBOUND_UNIT_PRODUCT_STOCK_ERROR = "update_inbound_unit_product_stock_error_toast";
const OUTBOUND_SUCCESS = "outbound_success_toast";

export function useOutboundClaimedProduct() {
  const { showToastUI } = useCustomToast();
  const { sendOutboundUpdate, sendOutboundOrderCancellationUpdate } = useInboundEventTracker();

  const [isStockUpdating, setIsStockUpdating] = useState(false);
  const {
    droppingListId,
    droppingListName,
    deliverySSCC,
    droppingListType,
    claimedProductsMap,
    claimedProductsStockUpdates,
    orderId,
    updateInboundQuantityForDisplay,
    removeInboundUnitFromDroppingList,
    setInboundActiveItem,
  } = useInboundStore(
    useShallow((state) => ({
      droppingListId: state.droppingListId,
      droppingListName: state.droppingListName,
      deliverySSCC: state.deliverySSCC,
      droppingListType: state.droppingListType,
      claimedProductsMap: state.claimedProductsMap,
      claimedProductsStockUpdates: state.claimedProductsStockUpdates,
      orderId: state.orderId,
      updateInboundQuantityForDisplay: state.updateInboundQuantityForDisplay,
      removeInboundUnitFromDroppingList: state.removeInboundUnitFromDroppingList,
      setInboundActiveItem: state.setInboundActiveItem,
    })),
  );
  const [batchUpdateStockForSkuMutation] = useBatchUpdateStockForSkuMutation();
  const [editPreDropQuantity] = useEditProductsInInboundingListMutation();
  const [deleteInboundingListProducts] = useDeleteInboundingListProductsMutation();
  const handleErrorToast = useCallback(
    (errorCode: string | undefined) => {
      if (errorCode === "INVENTORY_ENTRY_NOT_FOUND") {
        showToastUI({
          id: PRODUCT_NOT_ASSIGNED_ERROR,
          title: "hooks.use-inbound-action-implems.product-not-assigned-error",
        });
      } else if (errorCode === "INVENTORY_ENTRY_UNABLE_CREATE_SKU_HUB_ASSOCIATION") {
        showToastUI({
          id: UNABLE_TO_CREATE_SKU_HUB_ASSOCIATION_ERROR,
          title: "hooks.use-inbound-action-implems.association-creation-failed-error",
        });
      } else {
        showToastUI({
          id: UPDATE_INBOUND_UNIT_PRODUCT_STOCK_ERROR,
          title: "hooks.use-update-inbound-unit-product-stock.error-title",
          description: "hooks.use-update-inbound-unit-product-stock.error-description",
        });
      }
    },
    [showToastUI],
  );

  const updateStock = useCallback(
    async (
      sku: string,
      quantitiesByReason: StockUpdatePlan[],
      productComplaintMetadata?: ProductComplaintMetadata,
    ) => {
      setIsStockUpdating(true);
      try {
        const stockUpdateData = claimedProductsStockUpdates[sku];

        if (!stockUpdateData) throw new Error("Missing stock update plan");
        const isCancelledOrder = droppingListType === CommonListType.list_type_cancelled_order;
        const inventoryChangeReason = isCancelledOrder
          ? InventoryChangeReason.inbound_order_cancellation
          : InventoryChangeReason.inbound_goods_received;
        const currentInboundStock = stockUpdateData.stockUpdatePlan.find(
          (update) => update.reason === inventoryChangeReason,
        );

        if (!currentInboundStock) throw new Error("No inbound goods received entry found");

        const inboundQuantityDelta = currentInboundStock.quantityDelta;
        if (inboundQuantityDelta === 0) throw new Error("Stock updates must contain inbound goods");

        const filteredQuantitiesByReason = quantitiesByReason.filter(
          (plan) => plan.quantityDelta !== 0,
        );

        const totalOutbound = filteredQuantitiesByReason.reduce(
          (sum, { quantityDelta }) => sum + Math.abs(quantityDelta),
          0,
        );
        const newInboundQuantityDelta = inboundQuantityDelta - totalOutbound;
        const updatedStockUpdatePlan: StockUpdatePlan[] = [
          {
            reason: inventoryChangeReason,
            quantityDelta: totalOutbound,
          },
          ...filteredQuantitiesByReason,
        ];
        const { shelf } = claimedProductsMap[sku];

        if (!droppingListId) throw new Error("No droppingListId found");

        if (newInboundQuantityDelta === 0) {
          await deleteInboundingListProducts({
            variables: { input: { listId: droppingListId, skus: [sku] } },
          });
          setInboundActiveItem(sku);
          removeInboundUnitFromDroppingList(sku);
        } else {
          await editPreDropQuantity({
            variables: {
              input: {
                listId: droppingListId,
                products: [{ sku, quantity: newInboundQuantityDelta, handlingUnitSize: 1 }],
              },
            },
          });
          updateInboundQuantityForDisplay(sku, newInboundQuantityDelta);
        }
        await batchUpdateStockForSkuMutation({
          variables: {
            updateProductStockByDeltaAndMultipleReasonsInput: {
              sku,
              stockUpdates: updatedStockUpdatePlan.map(({ quantityDelta, reason }) => ({
                quantityDelta,
                reason,
                metadata: {
                  orderId,
                  ...(productComplaintMetadata
                    ? {
                        ...productComplaintMetadata,
                        imageUrls: productComplaintMetadata.imageUrls.join(","),
                      }
                    : {}),
                },
              })),
            },
          },
        });

        if (isCancelledOrder) {
          sendOutboundOrderCancellationUpdate({
            droppingListId,
            droppingListName,
            orderId,
            sku,
            newInboundQuantityDelta,
            updatedStockUpdatePlan,
          });
        } else {
          sendOutboundUpdate({
            listId: droppingListId,
            deliverySSCC,
            sku,
            updatedStockUpdatePlan,
            shelf,
          });
        }
        showToastUI({
          status: "success",
          id: OUTBOUND_SUCCESS,
          title: "toast.outbound.success.title",
          description: "toast.outbound.success.description",
        });
      } catch (error) {
        const errorCode =
          error instanceof ApolloError
            ? (error.graphQLErrors?.[0]?.extensions?.code as string | undefined)
            : "Unknown error";
        const errorMessage = error instanceof Error ? error.message : errorCode;
        datadogRum.addError("Error while outbounding the claimed product", {
          errorMessage,
        });
        handleErrorToast(errorCode);
      } finally {
        setIsStockUpdating(false);
      }
    },
    [
      batchUpdateStockForSkuMutation,
      claimedProductsMap,
      claimedProductsStockUpdates,
      deleteInboundingListProducts,
      deliverySSCC,
      droppingListId,
      droppingListName,
      droppingListType,
      editPreDropQuantity,
      handleErrorToast,
      orderId,
      removeInboundUnitFromDroppingList,
      sendOutboundOrderCancellationUpdate,
      sendOutboundUpdate,
      setInboundActiveItem,
      showToastUI,
      updateInboundQuantityForDisplay,
    ],
  );

  return { updateStock, isStockUpdating };
}
